import {omneticApi} from '@dms/api/base';

import {
  AddTemplateV3ApiArg,
  AddTemplateV3ApiResponse,
  CreateCustomFieldPropertyApiArg,
  CreateCustomFieldPropertyApiResponse,
  DownloadTemplateApiArg,
  DownloadTemplateApiArgV3,
  DownloadTemplateApiResponse,
  DownloadTemplateApiResponseV3,
  GetDefinitionTemplateListApiArg,
  GetDefinitionTemplateListApiResponse,
  GetDocumentDefinitionListApiArg,
  GetDocumentDefinitionListApiResponse,
  GetDocumentGroupListApiArg,
  GetDocumentGroupListApiResponse,
  HideTemplateApiArg,
  HideTemplateApiResponse,
  ListCustomFieldPropertyApiArg,
  ListCustomFieldPropertyApiResponse,
  PatchTemplateApiArg,
  PatchTemplateApiResponse,
  RemoveCustomFieldPropertyApiArg,
  RemoveCustomFieldPropertyApiResponse,
  RemoveTemplateApiArg,
  RemoveTemplateApiResponse,
  SetPrimaryAuthProfileApiArg,
  SetPrimaryAuthProfileApiResponse,
  SetPrimaryTemplateApiArgV3,
  SetPrimaryTemplateApiResponseV3,
  ShowTemplateApiArg,
  ShowTemplateApiResponse,
} from './types';

export const documentApi = omneticApi.injectEndpoints({
  endpoints: (build) => ({
    downloadTemplate: build.query<DownloadTemplateApiResponse, DownloadTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}/download`,
        responseHandler: async (response) => await response.blob(),
      }),
    }),
    createCustomFieldProperty: build.mutation<
      CreateCustomFieldPropertyApiResponse,
      CreateCustomFieldPropertyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/custom-field-document-template-property/${queryArg.customFieldDefinitionId}`,
        method: 'PUT',
        body: queryArg.createCustomFieldPropertyRequestBody,
      }),
      invalidatesTags: ['CustomFieldProperty'],
    }),
    removeCustomFieldProperty: build.mutation<
      RemoveCustomFieldPropertyApiResponse,
      RemoveCustomFieldPropertyApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v1/custom-field-document-template-property/${queryArg.customFieldDefinitionId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['CustomFieldProperty'],
    }),
    listCustomFieldProperty: build.query<
      ListCustomFieldPropertyApiResponse,
      ListCustomFieldPropertyApiArg
    >({
      query: () => ({
        url: `/dms/v1/custom-field-document-template-property`,
      }),
      providesTags: ['CustomFieldProperty'],
    }),
    getDefinitionTemplateList: build.query<
      GetDefinitionTemplateListApiResponse,
      GetDefinitionTemplateListApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v3/document/definition/template`,
        params: {
          withAuthorizationProfileId: queryArg.withAuthorizationProfileId,
          withoutAuthorizationProfile: queryArg.withoutAuthorizationProfile,
        },
      }),
      providesTags: ['DocumentTemplates'],
    }),
    getDocumentGroupList: build.query<GetDocumentGroupListApiResponse, GetDocumentGroupListApiArg>({
      query: () => ({
        url: `/dms/v3/document/group`,
      }),
    }),
    showTemplate: build.mutation<ShowTemplateApiResponse, ShowTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}/show`,
        method: 'POST',
      }),
      invalidatesTags: ['DocumentTemplates'],
    }),
    hideTemplate: build.mutation<HideTemplateApiResponse, HideTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}/hide`,
        method: 'POST',
      }),
      invalidatesTags: ['DocumentTemplates'],
    }),
    removeTemplate: build.mutation<RemoveTemplateApiResponse, RemoveTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['DocumentTemplates'],
    }),
    setPrimaryTemplateV3: build.mutation<
      SetPrimaryTemplateApiResponseV3,
      SetPrimaryTemplateApiArgV3
    >({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}/primary`,
        method: 'POST',
      }),
      invalidatesTags: ['DocumentTemplates'],
    }),
    downloadTemplateV3: build.query<DownloadTemplateApiResponseV3, DownloadTemplateApiArgV3>({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}/download`,
        responseHandler: async (response) => await response.blob(),
      }),
    }),
    setPrimaryAuthProfile: build.mutation<
      SetPrimaryAuthProfileApiResponse,
      SetPrimaryAuthProfileApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}/authorization-profile-primary/${queryArg.authorizationProfileId}`,
        method: 'POST',
      }),
      invalidatesTags: ['DocumentTemplates'],
    }),
    addTemplate: build.mutation<AddTemplateV3ApiResponse, AddTemplateV3ApiArg>({
      query: (queryArg) => ({
        url: `/dms/v3/document/template`,
        method: 'POST',
        body: queryArg.addTemplateRequestBody,
      }),
      invalidatesTags: ['DocumentTemplates'],
    }),
    patchTemplateV3: build.mutation<PatchTemplateApiResponse, PatchTemplateApiArg>({
      query: (queryArg) => ({
        url: `/dms/v3/document/template/${queryArg.templateId}`,
        method: 'PATCH',
        body: queryArg.patchTemplateRequestBody,
      }),
      invalidatesTags: ['DocumentTemplates'],
    }),
    getDocumentDefinitionList: build.query<
      GetDocumentDefinitionListApiResponse,
      GetDocumentDefinitionListApiArg
    >({
      query: (queryArg) => ({
        url: `/dms/v3/document/definition`,
        params: {group: queryArg.group},
      }),
      providesTags: ['DocumentTemplates'],
    }),
  }),
});

export const {
  useGetDocumentDefinitionListQuery,
  usePatchTemplateV3Mutation,
  useAddTemplateMutation,
  useRemoveTemplateMutation,
  useShowTemplateMutation,
  useHideTemplateMutation,
  useSetPrimaryTemplateV3Mutation,
  useDownloadTemplateV3Query,
  useLazyDownloadTemplateV3Query,
  useSetPrimaryAuthProfileMutation,
  useGetDocumentGroupListQuery,
  useGetDefinitionTemplateListQuery,
  useDownloadTemplateQuery,
  useLazyDownloadTemplateQuery,
  useCreateCustomFieldPropertyMutation,
  useRemoveCustomFieldPropertyMutation,
  useListCustomFieldPropertyQuery,
} = documentApi;
